import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import { ivyAffiliateApis } from "@services/apis/IvyAffiliate";
import { IvyAffiliateMerchant } from "@services/models/IvyAffiliate/GetIvyAffiliateMerchantsModel";
import { IvyAffiliate } from "@services/models/IvyAffiliate/GetIvyAffiliatesModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AssignIvyIntroducerSlider from "../../ivy-affiliate/components/AssignIvyIntroducerSlider";

const formatPct = (value: string | null | undefined) =>
  value === null || value === undefined ? "—" : value;

const MobileIvyAffiliateManagement = () => {
  const affiliatesGridRef = useRef<any>(null);
  const merchantsGridRef = useRef<any>(null);

  const [affiliates, setAffiliates] = useState<IvyAffiliate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignSliderOpen, setAssignSliderOpen] = useState(false);

  const [selectedAffiliate, setSelectedAffiliate] =
    useState<IvyAffiliate | null>(null);
  const [merchants, setMerchants] = useState<IvyAffiliateMerchant[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantsError, setMerchantsError] = useState("");

  const affiliatesColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "User ID",
        field: "userId",
        minWidth: 90,
        cellRenderer: ({ data }: { data: IvyAffiliate }) => (
          <div className="flex items-center justify-between space-x-[6px]">
            <span>{data.userId}</span>
            <CopyButton
              copyText={data.userId.toString()}
              copyMessage="User ID copied!"
              copyIconSize={10}
            />
          </div>
        ),
      },
      {
        headerName: "Email",
        field: "email",
        flex: 1.4,
        minWidth: 140,
        cellRenderer: ({ data }: { data: IvyAffiliate }) => (
          <span className="truncate">{data.email || "—"}</span>
        ),
      },
      {
        headerName: "Merchants",
        field: "merchantsCount",
        minWidth: 90,
      },
    ],
    [],
  );

  const merchantsColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Merchant ID",
        field: "merchantId",
        minWidth: 90,
      },
      {
        headerName: "Name",
        field: "merchantName",
        flex: 1.2,
        minWidth: 120,
        valueFormatter: (params) => params.value || "—",
      },
      {
        headerName: "Pay-in %",
        field: "payinRefPct",
        minWidth: 90,
        valueFormatter: (params) => formatPct(params.value),
      },
      {
        headerName: "Pay-out %",
        field: "payoutRefPct",
        minWidth: 90,
        valueFormatter: (params) => formatPct(params.value),
      },
    ],
    [],
  );

  const fetchAffiliates = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ivyAffiliateApis.getIvyAffiliates();
      setAffiliates(response.data || []);
    } catch (err: any) {
      setAffiliates([]);
      setError(
        err?.response?.data?.msg || "Failed to retrieve Ivy affiliates",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMerchants = useCallback(async (userId: number) => {
    try {
      setMerchantsLoading(true);
      setMerchantsError("");
      const response = await ivyAffiliateApis.getIvyAffiliateMerchants(userId);
      setMerchants(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setMerchants([]);
      setMerchantsError(
        err?.response?.data?.msg ||
          "Failed to retrieve Ivy affiliate merchants",
      );
    } finally {
      setMerchantsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  useEffect(() => {
    if (selectedAffiliate) {
      fetchMerchants(selectedAffiliate.userId);
    } else {
      setMerchants([]);
      setMerchantsError("");
    }
  }, [selectedAffiliate, fetchMerchants]);

  const handleAssignSuccess = () => {
    fetchAffiliates();
    if (selectedAffiliate) {
      fetchMerchants(selectedAffiliate.userId);
    }
  };

  const assignSlider = (
    <AssignIvyIntroducerSlider
      isOpen={assignSliderOpen}
      onClose={() => setAssignSliderOpen(false)}
      onSuccess={handleAssignSuccess}
    />
  );

  if (selectedAffiliate) {
    return (
      <div className="flex flex-col h-full">
        <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex items-center gap-3 px-4 sticky top-0 z-10">
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-transparent p-0 cursor-pointer"
            onClick={() => setSelectedAffiliate(null)}
            aria-label="Back"
          >
            <span className="material-symbols-outlined text-[22px] text-text-base">
              arrow_back
            </span>
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-text-base text-[14px] font-semibold truncate">
              Mapped Merchants
            </p>
            <p className="text-text-subtle text-[11px] truncate">
              {selectedAffiliate.email}
            </p>
          </div>
        </div>

        <div
          className="bg-bg-page-body p-3 flex-1"
          style={{ height: "calc(100dvh - 112px)" }}
        >
          {merchantsLoading && merchants.length === 0 ? (
            <AgGridSkeleton
              columnDefs={merchantsColumnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={merchantsGridRef}
              columnDefs={merchantsColumnDefs}
              rowData={merchants}
              getRowId={(params) => params.data.merchantId.toString()}
              error={merchantsError}
            />
          )}
        </div>
        {assignSlider}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex items-center justify-between px-4 sticky top-0 z-10 gap-2">
        <p className="text-text-base text-[14px] font-semibold shrink-0">
          Ivy Affiliate
        </p>
        <LightCustomButton
          text="ASSIGN"
          onClick={() => setAssignSliderOpen(true)}
          isActive={true}
          className="min-w-[80px]"
        />
      </div>

      <div
        className="bg-bg-page-body p-3 flex-1"
        style={{ height: "calc(100dvh - 112px)" }}
      >
        {loading && affiliates.length === 0 ? (
          <AgGridSkeleton
            columnDefs={affiliatesColumnDefs}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <AgGridReactTable
            ref={affiliatesGridRef}
            columnDefs={affiliatesColumnDefs}
            rowData={affiliates}
            onRowClicked={(params) =>
              setSelectedAffiliate(params.data as IvyAffiliate)
            }
            getRowId={(params) => params.data.userId.toString()}
            error={error}
          />
        )}
      </div>
      {assignSlider}
    </div>
  );
};

export default MobileIvyAffiliateManagement;
